// Serialises a live-coded match (DVMatchCoding state) into DataVolley
// `[3DATAVOLLEYSCOUT]` text so it can be stored via storeSession() and later
// re-parsed by generateMatch() in DVWFile.js for analysis/editing.
//
// The text layout mirrors exactly what generateMatch() reads:
//   [3MATCH]            date;time;;tournament; ... (>=13 fields)
//   [3TEAMS]            code;name; ...           (>=7 fields, home then away)
//   [3MORE]             ;homeSetter;awaySetter   (sets the primary setters)
//   [3SET]              ;;;;HS-AS;duration;      (one per set; >=7 fields)
//   [3PLAYERS-H/-V]     ;shirt;...;last;first;nick;;pos;; (>=16 fields)
//   [3ATTACKCOMBINATION]/[3SETTERCALL]  (headers only – optional content)
//   [3SCOUT]            scout code lines + score markers + lineup lines
//                       + setter lines (*P/aP set the setters, *z/az track the
//                         setter court position per rally) + **Nset end markers
//
// Scout code character positions consumed by the parser:
//   0      team marker (* = home/A, a = away/B)
//   1-2    shirt number (2 digits)
//   3      skill (S R E A B D F)
//   4      type / sub-event (1 char, required to keep fixed positions)
//   5      evaluation (= / - ! + #)
//   6-7    attack-combo code (serve/attack only)
//   8      tempo placeholder (serve/attack only)
//   9      start zone (serve/attack only)
//   10     end zone (serve/attack only)
//   11     end sub-zone A/B/C/D (optional)

const ETS_SKILLS = ["S", "R", "E", "A", "B", "D", "F"];

function san(value) {
  return String(value ?? "")
    .replace(/[;\r\n]/g, " ")
    .trim();
}

// Returns the first argument that is a non-empty / non-whitespace string after
// sanitising.  `??` chains stop on empty strings ("" ?? x === ""), so player
// fields that exist but are blank (common for DB players whose LastName is "")
// would otherwise win over a populated NickName/FirstName.
function firstNonEmptySan(...values) {
  for (const value of values) {
    const s = san(value);
    if (s) return s;
  }
  return "";
}

function pad2(value) {
  const n = parseInt(String(value), 10);
  if (Number.isNaN(n)) return "00";
  return String(n).padStart(2, "0");
}

// Normalise any CSS colour (named like "lime" or hex) to a lowercase
// "#rrggbb" string so team colours can be round-tripped through the DVW
// [3TEAMS] section (the reader only accepts a "#rrggbb" value there). Returns
// "" when the colour can't be resolved. Uses a static map for the handful of
// named colours the app can produce (the colour picker itself emits hex) so it
// needs no browser `canvas`.
const NAMED_COLOURS = {
  lime: "#00ff00",
  magenta: "#ff00ff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  cyan: "#00ffff",
  orange: "#ffa500",
  purple: "#800080",
  black: "#000000",
  white: "#ffffff",
  gray: "#808080",
  grey: "#808080",
};
function toHexColour(colour) {
  const c = String(colour ?? "").trim();
  if (!c) return "";
  if (/^#[0-9a-f]{6}$/i.test(c)) return c.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(c)) {
    return (
      "#" +
      c
        .slice(1)
        .split("")
        .map((x) => x + x)
        .join("")
        .toLowerCase()
    );
  }
  return NAMED_COLOURS[c.toLowerCase()] ?? "";
}

function toDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function fmtDate(value) {
  const d = toDate(value);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

function fmtTime(value) {
  const d = toDate(value);
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}.${mi}.${ss}`;
}

// DataVolley position codes used in the [3PLAYERS-*] sm[13] field.
function positionCode(role) {
  switch (String(role ?? "").toUpperCase()) {
    case "L":
      return "1"; // Libero
    case "OH":
    case "PASS/HITTER":
    case "R": // receiver/outside
      return "2";
    case "O":
    case "OPP":
    case "OPPOSITE":
      return "3";
    case "MB":
    case "M":
    case "MIDDLE":
      return "4";
    case "S":
    case "SETTER":
      return "5";
    default:
      return "";
  }
}

function playerShirt(player) {
  return player?.shirtNumber ?? player?.ShirtNumber ?? player?.playerNumber ?? null;
}

function playerFirst(player) {
  return player?.FirstName ?? player?.firstName ?? "";
}

function playerLast(player) {
  return player?.LastName ?? player?.lastName ?? "";
}

function playerNick(player) {
  return (
    player?.NickName ?? player?.nickName ?? playerFirst(player) ?? ""
  );
}

// Build the [3PLAYERS-*] line for one player (>=16 fields).
function playerLine(player) {
  const shirt = pad2(playerShirt(player));
  // Prefer the real last name, then fall back to nick/first name (and the
  // stable PlayerName captured on events) before the `P##` placeholder, so a
  // player who only has a NickName/FirstName still gets a readable name.
  const last =
    firstNonEmptySan(
      playerLast(player),
      playerNick(player),
      playerFirst(player),
      player?.PlayerName,
      player?.playerName
    ) || `P${shirt}`;
  const first = firstNonEmptySan(playerFirst(player)) || `Player`;
  // sm[10] === "Player" makes createPlayer skip the line, so avoid that exact value.
  const firstSafe = first === "Player" ? "Player " : first;
  const nick = firstNonEmptySan(playerNick(player), last) || last;
  const pos = positionCode(player?.role1);
  const fields = new Array(16).fill("");
  fields[1] = shirt; // sm[1] shirt number
  fields[9] = last; // sm[9] last name
  fields[10] = firstSafe; // sm[10] first name
  fields[11] = nick; // sm[11] nick name
  fields[13] = pos; // sm[13] position code
  return fields.join(";");
}

// True when a player object carries no usable name (so we should try to
// recover one from an event that references the same shirt number).
function hasPlayerName(player) {
  return !!firstNonEmptySan(
    playerLast(player),
    player?.NickName,
    player?.nickName,
    playerFirst(player),
    player?.PlayerName,
    player?.playerName
  );
}

// Resolve the roster for a team, deriving placeholder players from the events
// when no roster was supplied (so fetchPlayerInMatch finds every shirt number).
// Events carry the resolved `Player` object (and a stable `PlayerName`) captured
// while the roster was available, so prefer those names over a bare placeholder.
function resolveRoster(roster, sets, teamKey) {
  const byShirt = new Map();
  (roster ?? []).forEach((p) => {
    const shirt = playerShirt(p);
    if (shirt != null && shirt !== "--") {
      byShirt.set(pad2(shirt), p);
    }
  });
  (sets ?? []).forEach((set) => {
    (set.events ?? []).forEach((ev) => {
      if (ev.team !== teamKey) return;
      const shirt = ev.shirtNumber;
      if (shirt == null || shirt === "--") return;
      const key = pad2(shirt);
      // The player resolved on the event, enriched with the stable PlayerName
      // primitive so a placeholder still serialises a readable last name.
      const eventPlayer = ev.Player ?? ev.player ?? null;
      const namedEventPlayer = eventPlayer
        ? { ...eventPlayer, PlayerName: eventPlayer.PlayerName ?? ev.PlayerName }
        : ev.PlayerName
        ? { shirtNumber: parseInt(key, 10), PlayerName: ev.PlayerName }
        : null;
      const existing = byShirt.get(key);
      if (!existing) {
        byShirt.set(
          key,
          namedEventPlayer ?? { shirtNumber: parseInt(key, 10) }
        );
      } else if (!hasPlayerName(existing) && namedEventPlayer && hasPlayerName(namedEventPlayer)) {
        // Upgrade a nameless roster entry with the name captured on the event.
        byShirt.set(key, { ...existing, ...namedEventPlayer });
      }
    });
  });
  return Array.from(byShirt.values());
}

// Build a single scout event code from a coded event.  Returns null for events
// that are not player skills the parser understands (cover, manual point).
function eventCode(ev) {
  if (!ev || !ev.skill) return null;
  const skill = ev.skill;
  // Manual point (the "Point ▲ Team" buttons — skill "P"): DataVolley has no
  // "P" skill, so it used to be dropped entirely (eventCode returned null),
  // losing the rally-ending event AND its side-out rotation on reload while
  // the score still round-tripped via the `*p` markers. Emit it as a
  // player-less `*00P#`/`a00P#` code; DVWFile.js recognises "P" and recreates
  // it (see kSkillManualPoint) so the resume side-out replay counts it.
  if (skill === "P") {
    const teamChar = ev.team === "A" ? "*" : "a";
    const evalChar = ev.evaluation ?? ev.DVGrade ?? "#";
    return `${teamChar}00P~${evalChar}`;
  }
  // Substitution (skill "C" / EventType 250): DataVolley records these as a
  // "*cOUT:IN" / "acOUT:IN" line. The writer used to drop them (skill "C" isn't
  // in ETS_SKILLS), so the substitution count and history were LOST on a
  // save + resume-from-DB. Emit them here (DVWFile.js already parses them back)
  // in the exact fixed layout the parser expects: char0 = team, char1 = "c",
  // chars 2-3 = out#, char4 = ":", chars 5-6 = in#.
  if (
    ev.EventType === 250 ||
    ev.eventTypeOverride === 250 ||
    (skill === "C" &&
      /c\s*\d+\s*:\s*\d+/i.test(String(ev.substitution ?? ev.raw ?? "")))
  ) {
    const m = String(ev.substitution ?? ev.raw ?? ev.dvString ?? "").match(
      /c\s*0*(\d+)\s*:\s*0*(\d+)/i,
    );
    if (!m) return null;
    const teamChar = ev.team === "A" ? "*" : "a";
    return `${teamChar}c${pad2(m[1])}:${pad2(m[2])}`;
  }
  if (!ETS_SKILLS.includes(skill)) return null; // C (cover) etc.
  const teamChar = ev.team === "A" ? "*" : "a";
  const num =
    ev.shirtNumber != null && ev.shirtNumber !== "--"
      ? pad2(ev.shirtNumber)
      : "00";
  const type = ev.type && String(ev.type).length === 1 ? ev.type : "H";
  const evalChar = ev.evaluation ?? ev.DVGrade ?? "+";
  let code = teamChar + num + skill + type + evalChar;
  if (skill === "S" || skill === "A") {
    const sz = ev.zoneFrom ? String(ev.zoneFrom) : "";
    const ez = ev.zoneTo ? String(ev.zoneTo) : "";
    if (sz && ez) {
      // 6-7 combo, 8 tempo, 9 start zone, 10 end zone, 11 optional sub-zone.
      code += "~~~" + sz + ez;
      const subz = (ev.subzoneTo ?? "").toUpperCase();
      if (/^[A-D]$/.test(subz)) code += subz;
    }
  }
  const customSuffix = ev.customCode || (skill === "S" ? ev.powerPlayCode : null);
  if (customSuffix) {
    // Custom code (VolleyStation's "custom" field, or a Power Play call for a
    // serve) goes right after the fixed-position zone/subzone/extra-code
    // fields (position 15) — pad with the same placeholder DataVolley/
    // VolleyStation itself uses so the parser's end-of-string reads (DVWFile.js
    // ev.customCode / extractPowerPlayCode) line up the same way whether or
    // not zone data was present above.
    while (code.length < 15) code += "~";
    code += customSuffix;
  }
  return code;
}

// Returns "A" / "B" / null: which team won the rally that this event concluded.
function rallyWinnerFor(ev) {
  const skill = ev.skill;
  const team = ev.team;
  const evaluation = ev.evaluation ?? ev.DVGrade ?? null;
  const opp = team === "A" ? "B" : "A";
  if (skill === "P" && evaluation === "#") return team; // manual point
  if (evaluation === "=") return opp;
  if (evaluation === "#") {
    if (skill === "S" || skill === "A" || skill === "B") return team;
  }
  return null;
}

// A scout line carries the code in sm[0], time in sm[7], game number in sm[8],
// the home/away setter court positions in sm[9]/sm[10], video position in
// sm[12], and the on-court lineup as the last 12 fields (sm[14..25] = 6 Team A
// positions followed by 6 Team B positions).  The parser reads the lineup as
// the final 12 non-empty fields and, on "*z"/"az" lines, reads the setter's
// shirt from the lineup slot at sm[9]/sm[10], so every line in the [3SCOUT]
// section must carry these fields and keep a constant column count.
function scoutLine(code, gameNo, time, vp, lineupNums, homeSetterPos, awaySetterPos) {
  const fields = new Array(26).fill("");
  fields[0] = code;
  fields[7] = time ?? "";
  fields[8] = String(gameNo);
  if (homeSetterPos) fields[9] = String(homeSetterPos);
  if (awaySetterPos) fields[10] = String(awaySetterPos);
  fields[12] = vp != null && vp !== "" ? String(vp) : "";
  const lu = lineupNums ?? [];
  for (let i = 0; i < 12; i++) {
    fields[14 + i] = lu[i] ?? "";
  }
  // Trailing ";" mirrors the DataVolley layout (an empty final field).
  return fields.join(";") + ";";
}

// Lineup payload line: code "*P01>LUp" then the 12 lineup numbers carried by
// every scout line (6 home + 6 away) as the last 12 non-empty fields.
function sixNumbers(lineup) {
  const nums = (lineup ?? [])
    .map((p) => playerShirt(p))
    .filter((n) => n != null && n !== "--")
    .map((n) => String(parseInt(String(n), 10)));
  return nums.length === 6 ? nums : null;
}

// Order a lineup by court position 1..6 (positions rotate during play), giving
// a 6-slot array of shirt numbers.  Returns null unless all six slots fill.
function sixByPosition(lineup) {
  const slots = ["", "", "", "", "", ""];
  (lineup ?? []).forEach((p) => {
    const pos = parseInt(String(p?.position ?? p?.currentPosition ?? ""), 10);
    const num = playerShirt(p);
    if (Number.isFinite(pos) && pos >= 1 && pos <= 6 && num != null && num !== "--") {
      slots[pos - 1] = String(parseInt(String(num), 10));
    }
  });
  if (slots.every((s) => s !== "")) return slots;
  // Fall back to array order when positions are absent (e.g. a flat lineup).
  return sixNumbers(lineup);
}

// The 12 trailing lineup fields (6 home + 6 away).  Missing slots are emitted
// as empty strings so the column count stays constant across every line.
function lineupTwelve(homeSix, awaySix) {
  const out = new Array(12).fill("");
  if (homeSix) for (let i = 0; i < 6; i++) out[i] = homeSix[i] ?? "";
  if (awaySix) for (let i = 0; i < 6; i++) out[6 + i] = awaySix[i] ?? "";
  return out;
}

// Build the 12 lineup fields from an event's per-event LineupSnapshot
// ({ A: [6], B: [6] }) captured at coding time.  Returns null when absent.
function snapshotTwelve(ev) {
  const snap = ev?.LineupSnapshot;
  if (!snap || !Array.isArray(snap.A) || !Array.isArray(snap.B)) return null;
  const norm = (arr) =>
    arr.map((n) => (n == null || n === "" ? "" : String(parseInt(String(n), 10))));
  return lineupTwelve(norm(snap.A).slice(0, 6), norm(snap.B).slice(0, 6));
}

// Find the court position (1..6) of the given setter shirt within the 12-number
// lineup tail (first 6 = home, last 6 = away).  Returns null when the setter is
// not currently on court so the caller can keep the last known position.
function setterPositionInTwelve(twelve, setterShirt, isHome) {
  if (setterShirt == null || setterShirt === "" || setterShirt === "--") return null;
  const norm = String(parseInt(String(setterShirt), 10));
  if (norm === "NaN") return null;
  const base = isHome ? 0 : 6;
  for (let i = 0; i < 6; i++) {
    if (twelve[base + i] === norm) return i + 1;
  }
  return null;
}

/**
 * Serialise a coded match into DataVolley scout text.
 *
 * @param {Object} data
 * @param {string} data.teamAName
 * @param {string} data.teamBName
 * @param {string} [data.tournamentName]
 * @param {Date}   [data.date]
 * @param {Array}  data.rosterA  full roster for team A
 * @param {Array}  data.rosterB  full roster for team B
 * @param {number|string} [data.setterA]
 * @param {number|string} [data.setterB]
 * @param {Array}  data.sets  [{ pointsA, pointsB, events:[...], lineupA, lineupB }]
 * @returns {string} the `.dvw` text
 */
export function buildDVWMatchText(data) {
  const date = toDate(data.date ?? new Date());
  const lines = [];
  lines.push("[3DATAVOLLEYSCOUT]");

  // --- [3MATCH] ---
  lines.push("[3MATCH]");
  const matchFields = new Array(13).fill("");
  matchFields[0] = fmtDate(date);
  matchFields[1] = fmtTime(date);
  matchFields[3] = san(data.tournamentName) || "";
  // sm[4] = match GUID (round-trip persistence), sm[5/6] = home/away libero jerseys
  if (data.guid) matchFields[4] = String(data.guid);
  const fmtLiberos = (val) => {
    const arr = Array.isArray(val) ? val : (val != null && val !== "" ? [val] : []);
    return arr.map((n) => String(parseInt(String(n), 10))).filter((n) => n !== "NaN").join(",");
  };
  matchFields[5] = fmtLiberos(data.liberoA);
  matchFields[6] = fmtLiberos(data.liberoB);
  lines.push(matchFields.join(";"));

  // --- [3TEAMS] ---
  lines.push("[3TEAMS]");
  const teamHome = new Array(7).fill("");
  teamHome[0] = "H";
  teamHome[1] = san(data.teamAName) || "Team A";
  // Team colour round-trip: DataVolley leaves this field empty, and the parser
  // only picks it up when it looks like a "#rrggbb" hex value, so it never
  // clobbers a real imported file. Without it, colours reverted to defaults on
  // reload.
  const colourAHex = toHexColour(data.colourA);
  if (colourAHex) teamHome[6] = colourAHex;
  lines.push(teamHome.join(";"));
  const teamAway = new Array(7).fill("");
  teamAway[0] = "A";
  teamAway[1] = san(data.teamBName) || "Team B";
  const colourBHex = toHexColour(data.colourB);
  if (colourBHex) teamAway[6] = colourBHex;
  lines.push(teamAway.join(";"));

  // --- [3MORE] (primary setters) ---
  lines.push("[3MORE]");
  const moreFields = ["", "", ""];
  if (data.setterA != null) moreFields[1] = String(parseInt(String(data.setterA), 10));
  if (data.setterB != null) moreFields[2] = String(parseInt(String(data.setterB), 10));
  lines.push(moreFields.join(";"));

  // --- [3SET] (one line per played set) ---
  lines.push("[3SET]");
  // A set with no points/events yet is still included when BOTH teams'
  // starting lineups are fully set (6 players each) — otherwise a match
  // saved right after lineups are entered but before the first rally is
  // coded (see DVMatchCoding's "auto-save once lineups are filled" flow)
  // would have nowhere to carry the starting lineup: it's only ever
  // serialised via the `[3SCOUT]` lineup/setter lines emitted per set below,
  // which never run for a set excluded here — so the lineup was silently
  // lost on the next "Continue coding" reparse.
  const playedSets = (data.sets ?? []).filter(
    (s) =>
      (s.pointsA ?? 0) > 0 ||
      (s.pointsB ?? 0) > 0 ||
      (s.events ?? []).length > 0 ||
      (sixByPosition(s.lineupA) && sixByPosition(s.lineupB))
  );
  playedSets.forEach((set, idx) => {
    const setFields = new Array(7).fill("");
    setFields[0] = String(idx + 1);
    setFields[4] = `${set.pointsA ?? 0}-${set.pointsB ?? 0}`;
    setFields[5] = "0"; // duration (minutes) – unknown for live coding
    lines.push(setFields.join(";"));
  });

  // --- [3PLAYERS-H] / [3PLAYERS-V] ---
  const rosterA = resolveRoster(data.rosterA, playedSets, "A");
  const rosterB = resolveRoster(data.rosterB, playedSets, "B");
  lines.push("[3PLAYERS-H]");
  rosterA.forEach((p) => lines.push(playerLine(p)));
  lines.push("[3PLAYERS-V]");
  rosterB.forEach((p) => lines.push(playerLine(p)));

  // --- Combos / setter calls (headers only) ---
  lines.push("[3ATTACKCOMBINATION]");
  lines.push("[3SETTERCALL]");

  // --- [3SCOUT] ---
  lines.push("[3SCOUT]");
  const setterAShirt =
    data.setterA != null && data.setterA !== "" && data.setterA !== "--"
      ? String(parseInt(String(data.setterA), 10))
      : null;
  const setterBShirt =
    data.setterB != null && data.setterB !== "" && data.setterB !== "--"
      ? String(parseInt(String(data.setterB), 10))
      : null;
  playedSets.forEach((set, idx) => {
    const gameNo = idx + 1;

    // Starting lineup for this set (ordered by court position 1..6).
    const startHome = sixByPosition(set.lineupA);
    const startAway = sixByPosition(set.lineupB);
    const startLineup = lineupTwelve(startHome, startAway);

    // The lineup carried on each line evolves with rotations/substitutions:
    // each event supplies its own on-court snapshot; lines without one inherit
    // the most recent lineup seen.
    const firstSnap =
      (set.events ?? []).map((ev) => snapshotTwelve(ev)).find(Boolean) ?? null;
    let lastLineup = firstSnap ?? startLineup;

    // Track each setter's court position so the parser can resolve the setter
    // (and rotation) for every rally.  null when the setter is unknown.
    // Sources that know the TRUE current setter position per rally (VSM, whose
    // "setterAt" survives a mid-match setter substitution) attach it directly
    // on each event as ev.setterPosA/B — prefer that over inferring it from a
    // single tracked shirt's lineup position, which goes stale (frozen) the
    // moment that shirt is substituted out.
    const firstSetterPosA = (set.events ?? []).find((e) => e.setterPosA != null)?.setterPosA;
    const firstSetterPosB = (set.events ?? []).find((e) => e.setterPosB != null)?.setterPosB;
    let homeSetterPos =
      firstSetterPosA ?? (setterAShirt ? setterPositionInTwelve(lastLineup, setterAShirt, true) : null);
    let awaySetterPos =
      firstSetterPosB ?? (setterBShirt ? setterPositionInTwelve(lastLineup, setterBShirt, false) : null);

    // Whether the resolved lineup fills all six home / away slots (it may come
    // from set.lineupA/B or from the first event's snapshot).
    const homeFull = lastLineup.slice(0, 6).every((x) => x !== "");
    const awayFull = lastLineup.slice(6, 12).every((x) => x !== "");

    // Lineup payload line (drives rotation-based stats and carries both teams'
    // lineups) when both lineups hold exactly six players.  The parser requires
    // this code to be exactly "*P##>LUp" (length 8) to read the lineup.
    const luSetterCode =
      setterAShirt && parseInt(setterAShirt, 10) <= 99 ? pad2(setterAShirt) : "01";
    if (homeFull && awayFull) {
      lines.push(
        scoutLine(`*P${luSetterCode}>LUp`, gameNo, "", "", lastLineup, homeSetterPos, awaySetterPos)
      );
    }

    // Setter assignment lines at the start of the set: "*z" (home) / "az" (away)
    // tell the parser the setter's court position; "aP" mirrors DataVolley.
    if (homeSetterPos) {
      lines.push(
        scoutLine(`*z${homeSetterPos}>LUp`, gameNo, "", "", lastLineup, homeSetterPos, awaySetterPos)
      );
    }
    if (setterBShirt && awayFull) {
      lines.push(
        scoutLine(`aP${pad2(setterBShirt)}>LUp`, gameNo, "", "", lastLineup, homeSetterPos, awaySetterPos)
      );
    }
    if (awaySetterPos) {
      lines.push(
        scoutLine(`az${awaySetterPos}>LUp`, gameNo, "", "", lastLineup, homeSetterPos, awaySetterPos)
      );
    }

    let home = 0;
    let away = 0;
    lines.push(scoutLine(`*p${home}:${away}`, gameNo, "", "", lastLineup, homeSetterPos, awaySetterPos));

    (set.events ?? []).forEach((ev) => {
      const time = ev.TimeStamp ? fmtTime(ev.TimeStamp) : "";
      const evLineup = snapshotTwelve(ev) ?? lastLineup;
      lastLineup = evLineup;

      // A team rotates (its setter moves) at the start of a new rally; emit a
      // "*z"/"az" line whenever the setter's court position changes so the
      // parser tracks the current setter for the rallies that follow. Prefer
      // an explicit per-event override (ev.setterPosA/B — e.g. VSM's own
      // per-rally "setterAt", true even across a mid-match setter sub) over
      // inferring it from where a single tracked shirt sits in the lineup.
      const homeNewPos =
        ev.setterPosA ?? (setterAShirt ? setterPositionInTwelve(evLineup, setterAShirt, true) : null);
      if (homeNewPos && homeNewPos !== homeSetterPos) {
        homeSetterPos = homeNewPos;
        lines.push(scoutLine(`*z${homeSetterPos}`, gameNo, time, "", evLineup, homeSetterPos, awaySetterPos));
      }
      const awayNewPos =
        ev.setterPosB ?? (setterBShirt ? setterPositionInTwelve(evLineup, setterBShirt, false) : null);
      if (awayNewPos && awayNewPos !== awaySetterPos) {
        awaySetterPos = awayNewPos;
        lines.push(scoutLine(`az${awaySetterPos}`, gameNo, time, "", evLineup, homeSetterPos, awaySetterPos));
      }

      // VIS events carry the absolute rally score on every ev.scoreA/scoreB.
      // Emit *p before the event code so DVWFile captures the correct score;
      // skip rallyWinnerFor entirely (no double-counting, no pre/post mismatch).
      // Regular DVW events (ev.scoreA == null) use the existing mechanism.
      if (ev.scoreA != null) {
        lines.push(scoutLine(`*p${ev.scoreA}:${ev.scoreB ?? 0}`, gameNo, time, "", evLineup, homeSetterPos, awaySetterPos));
      }
      const code = eventCode(ev);
      if (code) {
        lines.push(
          scoutLine(code, gameNo, time, ev.VideoPosition ?? "", evLineup, homeSetterPos, awaySetterPos)
        );
      }
      if (ev.scoreA == null) {
        const winner = rallyWinnerFor(ev);
        if (winner === "A") home += 1;
        else if (winner === "B") away += 1;
        if (winner) {
          lines.push(scoutLine(`*p${home}:${away}`, gameNo, time, "", evLineup, homeSetterPos, awaySetterPos));
        }
      }
    });

    // The per-rally running score reconstructed above is approximate (a rally
    // spans several skill events).  Emit a final corrective marker so the
    // parser records the true final set score on the drill.
    lines.push(
      scoutLine(
        `*p${set.pointsA ?? 0}:${set.pointsB ?? 0}`,
        gameNo,
        "",
        "",
        lastLineup,
        homeSetterPos,
        awaySetterPos
      )
    );

    // End-of-set marker: locks the drill and resets the parser's running score.
    lines.push(scoutLine(`**${gameNo}set`, gameNo, "", "", lastLineup, homeSetterPos, awaySetterPos));
  });

  lines.push("[3VIDEO]");
  lines.push("[3COMMENTS]");

  return lines.join("\r\n");
}
